/**
 * Recipes browse — mockup-aligned home (featured, categories, search, grid/list).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaFire, FaTh, FaList } from 'react-icons/fa';
import { communityRecipesService } from '../services/communityRecipesService';
import { YLogSearchInput } from '../../../ui/YLogSearchInput';
import { YSelect } from '../../../ui/YSelect';
import { YButton } from '../../../ui/YButton';
import {
  RECIPE_GAME_CATEGORIES,
  filterRecipes,
  sortRecipes,
  pickFeaturedRecipes,
  proteinLabel,
} from '../campRecipesFilters';
import './camp-essentials-shell.css';
import './camp-essentials-gear.css';
import './camp-recipes-shell.css';

const PAGE_SIZE = 24;

function RecipeMedia({ recipe, className }) {
  if (recipe.thumbnail_url) {
    return <img src={recipe.thumbnail_url} alt="" className={className} />;
  }
  return null;
}

function FeaturedRecipeCard({ recipe, onClick }) {
  return (
    <button type="button" className="camp-recipes-featured-card" onClick={onClick}>
      <div className="camp-recipes-featured-card__media">
        <RecipeMedia recipe={recipe} />
        <div className="camp-recipes-featured-card__overlay" aria-hidden="true" />
        <div className="camp-recipes-featured-card__overlay-content">
          {recipe.avg_stars != null ? (
            <span className="camp-recipes-featured-card__rating">
              <FaStar aria-hidden="true" />
              {recipe.avg_stars}
            </span>
          ) : null}
          <h3 className="camp-recipes-featured-card__title">{recipe.title}</h3>
          {recipe.description ? (
            <p className="camp-recipes-featured-card__desc">{recipe.description}</p>
          ) : null}
        </div>
      </div>
      <div className="camp-recipes-featured-card__footer">
        <span>
          {recipe.source === 'curated' ? 'Curated' : 'Community'}
          {recipe.rating_count ? ` · ${recipe.rating_count} ratings` : ''}
        </span>
        <span className="camp-recipes-featured-card__game">
          {recipe.main_protein_display || proteinLabel(recipe.main_protein)}
        </span>
      </div>
    </button>
  );
}

function RecipeCard({ recipe, viewMode, onClick }) {
  const isList = viewMode === 'list';
  return (
    <button
      type="button"
      className={`camp-recipes-card${isList ? ' camp-recipes-card--list' : ''}`}
      onClick={onClick}
    >
      <div className="camp-recipes-card__media">
        <RecipeMedia recipe={recipe} />
        <div className="camp-recipes-card__badges">
          <span className="camp-recipes-card__game-badge">
            {recipe.main_protein_display || proteinLabel(recipe.main_protein) || 'Recipe'}
          </span>
        </div>
        {recipe.avg_stars != null ? (
          <div className="camp-recipes-card__rating-row">
            <span className="camp-recipes-card__rating-badge">
              <FaStar aria-hidden="true" />
              {recipe.avg_stars}
            </span>
          </div>
        ) : null}
      </div>
      <div className="camp-recipes-card__body">
        <h3 className="camp-recipes-card__title">{recipe.title}</h3>
        {recipe.description ? <p className="camp-recipes-card__desc">{recipe.description}</p> : null}
        <div className="camp-recipes-card__meta">
          <span>
            {recipe.source === 'curated' ? 'Curated' : 'Community'}
            {recipe.rating_count ? ` · ${recipe.rating_count} ratings` : ''}
          </span>
        </div>
        <div className="camp-recipes-card__author">
          <span className="camp-recipes-card__author-dot" aria-hidden="true">
            {(recipe.main_protein_display || 'R').charAt(0)}
          </span>
          <span>{recipe.main_protein_display || 'Wild game'}</span>
        </div>
      </div>
    </button>
  );
}

export function CommunityRecipesHome({ totalCountLabel }) {
  const navigate = useNavigate();
  const [allRecipes, setAllRecipes] = useState([]);
  const [totalCommunity, setTotalCommunity] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryId, setCategoryId] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [viewMode, setViewMode] = useState('grid');

  const loadFirstPage = useCallback(() => {
    setLoading(true);
    setError('');
    communityRecipesService
      .feed({ limit: PAGE_SIZE, offset: 0 })
      .then((data) => {
        setAllRecipes(data.recipes || []);
        setTotalCommunity(typeof data.total_community === 'number' ? data.total_community : 0);
        setHasMore(Boolean(data.has_more));
        setNextOffset(typeof data.next_offset === 'number' ? data.next_offset : 0);
      })
      .catch(() => setError('Could not load recipes.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    communityRecipesService
      .feed({ limit: PAGE_SIZE, offset: nextOffset })
      .then((data) => {
        setAllRecipes((prev) => [...prev, ...(data.recipes || [])]);
        setHasMore(Boolean(data.has_more));
        setNextOffset(typeof data.next_offset === 'number' ? data.next_offset : nextOffset);
      })
      .catch(() => setError('Could not load more recipes.'))
      .finally(() => setLoadingMore(false));
  };

  const filteredSorted = useMemo(() => {
    const filtered = filterRecipes(allRecipes, { searchQuery, categoryId });
    return sortRecipes(filtered, sortBy);
  }, [allRecipes, searchQuery, categoryId, sortBy]);

  const featured = useMemo(() => pickFeaturedRecipes(allRecipes, 3), [allRecipes]);

  const openRecipe = (id) => navigate(`/camp-essentials/recipes/${id}`);

  if (loading) {
    return <p className="camp-recipes-empty">Loading recipes…</p>;
  }

  if (error && allRecipes.length === 0) {
    return <p className="camp-recipes-empty">{error}</p>;
  }

  return (
    <div className="camp-recipes-browse" data-testid="camp-recipes-home">
      {featured.length > 0 ? (
        <section className="camp-recipes-featured" aria-labelledby="featured-recipes-heading">
          <div className="camp-recipes-section__label" id="featured-recipes-heading">
            <FaFire aria-hidden="true" />
            Featured recipes
          </div>
          <div className="camp-recipes-featured__grid">
            {featured.map((r) => (
              <FeaturedRecipeCard key={r.id} recipe={r} onClick={() => openRecipe(r.id)} />
            ))}
          </div>
        </section>
      ) : null}

      <section aria-labelledby="browse-game-heading">
        <div className="camp-recipes-section__label" id="browse-game-heading">
          Browse by game
        </div>
        <div className="camp-recipes-categories" role="group" aria-label="Game type">
          {RECIPE_GAME_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`camp-recipes-categories__chip${
                categoryId === cat.id ? ' camp-recipes-categories__chip--active' : ''
              }`}
              onClick={() => setCategoryId(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      <section className="camp-recipes-toolbar" aria-label="Search and sort">
        <div className="camp-recipes-toolbar__search">
          <YLogSearchInput
            id="camp-recipes-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search recipes, ingredients, tags…"
            ariaLabel="Search recipes"
          />
        </div>
        <div className="camp-recipes-toolbar__actions">
          <YSelect
            className="camp-recipes-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort recipes"
          >
            <option value="popular">Most saved</option>
            <option value="rating">Top rated</option>
            <option value="newest">Newest</option>
          </YSelect>
          <div className="y-view-toggle" role="group" aria-label="Recipe layout">
            <button
              type="button"
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <FaTh size={14} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              aria-label="List view"
              aria-pressed={viewMode === 'list'}
            >
              <FaList size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      <section aria-labelledby="all-recipes-heading">
        <p className="camp-recipes-results__meta" id="all-recipes-heading">
          {filteredSorted.length} recipe{filteredSorted.length === 1 ? '' : 's'} found
          {totalCountLabel ||
            (totalCommunity > 0
              ? ` · ${totalCommunity} in the community`
              : '')}
        </p>
        {error && allRecipes.length > 0 ? (
          <p className="camp-recipes-empty">{error}</p>
        ) : null}

        {filteredSorted.length === 0 ? (
          <p className="camp-recipes-empty">
            No recipes match. Try another category or submit the first one for your crew.
          </p>
        ) : (
          <div
            className={`camp-recipes-grid camp-recipes-grid--${viewMode}`}
            data-testid="camp-recipes-grid"
          >
            {filteredSorted.map((r) => (
              <RecipeCard
                key={r.id}
                recipe={r}
                viewMode={viewMode}
                onClick={() => openRecipe(r.id)}
              />
            ))}
          </div>
        )}

        {hasMore && categoryId === 'all' && !searchQuery.trim() ? (
          <div className="camp-recipes-load-more">
            <YButton
              type="button"
              variant="outline-secondary"
              disabled={loadingMore}
              onClick={loadMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </YButton>
          </div>
        ) : null}
      </section>
    </div>
  );
}
